// Multiplayer relay hook: opens a WebSocket to sstk-studio-infra's relay
// (API Gateway WebSocket + Lambda + DynamoDB, relay.tf) for a published
// game's room and exposes presence plus generic send/receive.
//
// Scope boundary (see docs/game-maker-completion/design.md section 6 and
// requirements.md's non-goals): this is a shared-state broadcast relay, not
// an authoritative simulation - it does not validate, order or interpret
// what is sent. It is also deliberately NOT wired into the Logic executor
// (agent/02-gameplay-executor's separate, larger work is where authored
// Logic rules would read/write synchronised multiplayer state); the one
// thing built on top of it here is the presence list in PresenceList.tsx.
//
// Mirrors usePeerCursors.ts's shape (a plain hook, connection lifecycle
// owned entirely inside one effect with cleanup) - the closest existing
// precedent for a "real-time state from outside this tab" hook in this repo,
// even though that one is still a same-browser BroadcastChannel.
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchRelayToken } from './playerSession';

export interface RelayPresenceEntry {
  playerId: string;
  name: string;
}

export interface RelayMessage {
  from: RelayPresenceEntry;
  payload: unknown;
}

/**
 * The relay's late-join catch-up frame (docs/multiplayer-sync/roadmap.md
 * section 3.5 item 2, `sstk-studio-infra`'s `relay/src/index.js`): sent once
 * per connection, directly to the joining client alone, right after
 * `$connect` - never broadcast, so it never arrives as a `RelayMessage`.
 * `'pending'` until it arrives (every connection gets exactly one, so this
 * is a genuinely transient state, not a "maybe never" one); `'received'`
 * with `payload: null` is a real, distinct answer meaning "the relay has no
 * stored logic-snapshot for this room yet" (no host has broadcast shared
 * state, or this is the room's first session ever) - not the same thing as
 * still waiting.
 */
export type RelayInitialState =
  | { status: 'pending' }
  | { status: 'received'; payload: unknown };

const PENDING_INITIAL_STATE: RelayInitialState = { status: 'pending' };

/** `'unavailable'` is distinct from `'closed'`/`'failed'`: it means this
 * environment has no relay deployed for it at all (`fetchRelayToken`
 * resolved with an empty `wsUrl`) - a permanent, no-retry condition a
 * caller should treat like there is no relay concept here, ever.
 * `'closed'` means a real connection was attempted and dropped (or never
 * opened due to a token request failure) and `scheduleReconnect` is still
 * actively retrying it - a relay genuinely exists for this environment and
 * connecting is still in progress. `'failed'` means that retrying has been
 * abandoned (`MAX_RECONNECT_ATTEMPTS` exhausted, see `nextReconnectStatus`)
 * - also permanent for the life of this mount, like `'unavailable'`, but
 * distinct from it: a relay does genuinely exist here, it just could not be
 * reached this session. A caller deciding "is there any point waiting on
 * this relay for anything, including host election" should treat `'failed'`
 * the same way it already treats `'unavailable'` - see
 * `useMultiplayerSync.ts`'s own `resolveIsHost`, which does exactly that
 * rather than leaving a client permanently reporting `isHost: false` once a
 * connection it never actually re-opens is the only thing standing between
 * it and its own single-player-safe default. */
export type RelayStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'failed' | 'unavailable';

const RECONNECT_BASE_DELAY_MS = 2000;
const MAX_RECONNECT_ATTEMPTS = 5;

/** The status to move to once a reconnect attempt is (or is not) scheduled,
 * given how many attempts have already been made: `'closed'` while
 * `scheduleReconnect` still has attempts left to try (a timer is armed),
 * `'failed'` once `MAX_RECONNECT_ATTEMPTS` is exhausted and no further retry
 * will ever be scheduled for this mount. Pulled out as its own pure
 * function, same reasoning as `parseFrame`'s own doc comment - this is the
 * piece of `scheduleReconnect` that decides whether a caller is ever told
 * "give up on this relay", worth a direct test independent of the socket
 * lifecycle around it. */
export function nextReconnectStatus(attempts: number): 'closed' | 'failed' {
  return attempts >= MAX_RECONNECT_ATTEMPTS ? 'failed' : 'closed';
}

interface PresenceFrame {
  type: 'presence';
  players: RelayPresenceEntry[];
}

interface MessageFrame {
  type: 'message';
  from: RelayPresenceEntry;
  payload: unknown;
}

interface StateFrame {
  type: 'state';
  payload: unknown;
}

type RelayFrame = PresenceFrame | MessageFrame | StateFrame;

/** Exported for its own direct unit test - the one piece of this hook that
 * is pure and worth covering without rendering the whole hook/WebSocket
 * lifecycle (this repo has no React hook-testing utility installed; every
 * other tested piece of logic in this codebase is extracted and tested the
 * same way, e.g. `isPersistedWorkspaceState`/`diffTriggerContainment`). */
export function parseFrame(data: unknown): RelayFrame | null {
  if (typeof data !== 'string') return null;
  try {
    const parsed = JSON.parse(data) as { type?: unknown };
    if (parsed.type === 'presence' || parsed.type === 'message' || parsed.type === 'state') {
      return parsed as RelayFrame;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Connects to the relay room for `gameId` (undefined - e.g. before the
 * publication payload has loaded - is a no-op). Reconnects with a short
 * backoff on an unexpected close, up to MAX_RECONNECT_ATTEMPTS, fetching a
 * fresh relay token each time (tokens are short-lived by design). Never
 * throws: a relay that is not deployed for this environment (empty wsUrl)
 * leaves status 'unavailable' (permanent, never retried); an unreachable one
 * leaves status 'closed' while retries remain, then 'failed' once
 * MAX_RECONNECT_ATTEMPTS is exhausted (see `nextReconnectStatus`) - either
 * way presence stays empty, since this feature is additive - a published
 * game must still be playable with no relay present.
 */
export function useRelayConnection(gameId: string | undefined) {
  const [status, setStatus] = useState<RelayStatus>('idle');
  const [presence, setPresence] = useState<RelayPresenceEntry[]>([]);
  // A queue, not a single slot: `onmessage` is a raw WebSocket callback, so
  // React's `setState` from inside it is applied asynchronously (deferred to
  // React's own scheduler). If two 'message' frames' `onmessage` callbacks
  // both run before the first update flushes, a single-slot `useState`
  // would let React coalesce both into one render carrying only the second
  // value - the first message silently vanishes, taking whatever action it
  // should have triggered on the receiving client with it (a real, observed
  // race - not hypothetical, given `useMultiplayerSync.ts`'s own
  // `onSharedEffect` sends a 'logic-effect' message immediately followed by
  // a 'logic-snapshot' one, synchronously, with zero gap). Appending via the
  // functional updater form instead means every call always reads the
  // truly-latest queued array, so two (or more) messages queued before a
  // flush are always both kept, in order - never collapsed into one.
  const [messages, setMessages] = useState<RelayMessage[]>([]);
  const [initialState, setInitialState] = useState<RelayInitialState>(PENDING_INITIAL_STATE);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    let attempts = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = async () => {
      if (cancelled) return;
      setStatus('connecting');
      // A fresh connection gets its own fresh catch-up frame from the relay
      // shortly after opening - reset so a reconnect does not keep showing a
      // previous connection's now-possibly-stale state in the meantime.
      setInitialState(PENDING_INITIAL_STATE);

      let token: string;
      let wsUrl: string;
      try {
        ({ token, wsUrl } = await fetchRelayToken(gameId));
      } catch (err) {
        console.warn('[relay] token request failed:', err);
        scheduleReconnect();
        return;
      }
      if (cancelled) return;
      if (!wsUrl) {
        // No relay deployed for this environment - a distinct, permanent
        // status (never retried) rather than 'closed', so a caller can
        // tell "no relay, ever" apart from "a real connection dropped and
        // may still be retrying" (see RelayStatus's own doc comment).
        setStatus('unavailable');
        return;
      }

      const socket = new WebSocket(`${wsUrl}?token=${encodeURIComponent(token)}`);
      socketRef.current = socket;

      socket.onopen = () => {
        attempts = 0;
        setStatus('open');
      };
      socket.onmessage = (event) => {
        const frame = parseFrame(event.data);
        if (!frame) return;
        if (frame.type === 'presence') {
          setPresence(frame.players);
        } else if (frame.type === 'state') {
          setInitialState({ status: 'received', payload: frame.payload });
        } else {
          // Functional update, not `setMessages([...messages, ...])` closing
          // over this render's own `messages` - see this hook's own `messages`
          // doc comment above for the race this form specifically avoids.
          setMessages((prev) => [...prev, { from: frame.from, payload: frame.payload }]);
        }
      };
      socket.onclose = () => {
        if (socketRef.current === socket) socketRef.current = null;
        setPresence([]);
        if (cancelled) return;
        scheduleReconnect();
      };
      socket.onerror = () => socket.close();
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      // Previously this early-returned once attempts were exhausted, leaving
      // `status` sitting at whatever it was last set to ('closed', from the
      // caller just above) forever - permanently indistinguishable from
      // "still actively retrying", with no way for a consumer to ever learn
      // this relay has genuinely been given up on. `nextReconnectStatus`
      // always resolves a real terminal answer instead, so this always sets
      // *some* status, even on the attempt that gives up for good.
      setStatus(nextReconnectStatus(attempts));
      if (attempts >= MAX_RECONNECT_ATTEMPTS) return;
      attempts += 1;
      reconnectTimer = setTimeout(connect, RECONNECT_BASE_DELAY_MS * attempts);
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [gameId]);

  const send = useCallback((payload: unknown) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({ action: 'message', payload }));
  }, []);

  // Removes the first `count` queued messages, once a consumer has fully
  // processed exactly that many (in order) from the front of `messages` -
  // call this right after, so the queue does not grow unbounded for the
  // life of a long session. Takes a count to remove rather than resetting to
  // `[]` outright specifically so a message appended between "the consumer
  // read `messages`" and "the consumer calls this" (a real WebSocket frame,
  // asynchronously, mid-render) is never silently discarded: the functional
  // updater always slices against whatever `prev` genuinely is by the time
  // this runs, which may already include messages newer than the ones being
  // consumed - `slice(count)` keeps those, where `() => []` would not.
  const consumeMessages = useCallback((count: number) => {
    if (count <= 0) return;
    setMessages((prev) => prev.slice(count));
  }, []);

  return { status, presence, messages, initialState, send, consumeMessages };
}
